import { Box, Tooltip } from "@mui/material";
import Translate from "./Translate";
import {
  AccessTime,
  Block,
  Done,
  Event,
  EventAvailable,
  HourglassBottom,
  HourglassTop,
  Inventory,
  LocalShipping,
  MoreHoriz,
  PriceCheck,
  QuestionMark,
  RequestQuote,
  ScheduleSend,
  School,
  Send,
  Verified,
  Warning,
} from "@mui/icons-material";
import { isAutoPickPartType, isFreightPartType } from "./part";
import { paymentStatus } from "./payment";
import { formatDate } from "./dateTimeFormat";

export const orderStatus = {
  canceled: 0,
  registered: 10,
  registeredAutoOrder: 11,
  waitingForPrePayment: 19,
  ordered: 20,
  underPicking: 30,
  waitingForCourseInvoice: 35,
  picked: 40,
  pickedAutoOrder: 41,
  scheduledForInvoice: 49,
  waitingForInvoice: 50,
  sentToERP: 60,
  invoicedInERP: 65,
  partlyPaid: 70,
  paid: 80,
  finished: 90,
};

export function getOrderIconbox(
  status,
  orderPaymentStatus,
  overrideColor,
  isMini,
  isCourse,
  isMissingInfo,
  scheduledInvoiceDate,
  invoiceCanceled,
) {
  let color;
  let icon;

  let title = Translate.getOrderStatus(status);

  if (status < orderStatus.registered) {
    color = "warning";
    icon = <Warning />;
  } else if (status === orderStatus.waitingForPrePayment) {
    title = orderPaymentStatus
      ? `${Translate.getPaymentStatus(orderPaymentStatus)} (${Translate.get("PrePayment").toLowerCase()})`
      : title;
    color = orderPaymentStatus === paymentStatus.readyForInvoice ? "fuchsia" : "blue";
    icon = getPrePaymentIcon(orderPaymentStatus) ?? <QuestionMark />;
  } else if (status === orderStatus.waitingForCourseInvoice) {
    color = "blue";
    icon = <School />;
  } else if (status === orderStatus.underPicking) {
    color = "blue";
    icon = <Inventory />;
  } else if (status === orderStatus.picked) {
    if (isCourse) {
      color = "success";
      title = Translate.get("FinishedCourse");
      icon = <School />;
    } else {
      color = "blue";
      icon = <LocalShipping />;
    }
  } else if (status === orderStatus.pickedAutoOrder) {
    color = "blue";
    icon = <LocalShipping />;
  } else if (status === orderStatus.scheduledForInvoice) {
    title += scheduledInvoiceDate ? `: ${formatDate(scheduledInvoiceDate)}` : "";
    color = "blue";
    icon = <Event />;
  } else if (status === orderStatus.waitingForInvoice) {
    color = "blue";
    icon = <HourglassTop />;
  } else if (status === orderStatus.sentToERP) {
    color = "blue";
    icon = <HourglassBottom />;
  } else if (status === orderStatus.invoicedInERP) {
    color = "blue";
    icon = <RequestQuote />;
  } else if (status === orderStatus.partlyPaid) {
    color = "blue";
    icon = <RequestQuote />;
  } else if (status === orderStatus.paid) {
    color = "success";
    icon = <PriceCheck />;
  } else if (status === orderStatus.finished) {
    color = "success";
    icon = <Done />;
  }

  if (isMissingInfo && !icon && !title) {
    icon = <Warning />;
    color = "warning";
    title = Translate.get("OrderStatus_MissingInfo");
  } else {
    const ignoreMissingInfo = ignoreIsMissingInfo(status);
    color = overrideColor ?? (isMissingInfo && !ignoreMissingInfo ? "warning" : color);

    const addMissingInfoLabel = isMissingInfo && !ignoreMissingInfo;
    title = title + (addMissingInfoLabel ? ` (${Translate.get("OrderStatus_MissingInfo").toLowerCase()})` : "");

    const addPrepaymentLabel =
      status !== orderStatus.waitingForPrePayment && status < orderStatus.waitingForInvoice && !!orderPaymentStatus;
    title = title + (addPrepaymentLabel ? ` (${Translate.get("PrePayment").toLowerCase()})` : "");
  }

  if (invoiceCanceled) {
    color = overrideColor ?? "warning";
    title = !!title ? `${title} (${Translate.get("InvoicedCanceledInERP")})` : Translate.get("InvoicedCanceledInERP");
  }

  const className = `iconbox ${color} ${isMini && "mini"}`;

  if (color) {
    return (
      <Tooltip arrow className={className} title={title}>
        {icon}
      </Tooltip>
    );
  } else {
    return undefined;
  }
}

function getPrePaymentIcon(orderPaymentStatus) {
  switch (orderPaymentStatus) {
    case paymentStatus.canceled:
      return <Block />;
    case paymentStatus.faulted:
      return <Warning />;
    case paymentStatus.initialized:
      return <MoreHoriz />;
    case paymentStatus.readyForInvoice:
      return <Send />;
    case paymentStatus.directPaymentInProgress:
      return <AccessTime />;
    case paymentStatus.directPaidReadyForInvoice:
      return <Verified />;
    case paymentStatus.scheduledForInvoice:
      return <ScheduleSend />;
    case paymentStatus.waitingForInvoice:
      return <HourglassTop />;
    case paymentStatus.sentToERP:
      return <HourglassBottom />;
    case paymentStatus.invoicedInERP:
      return <RequestQuote />;
    case paymentStatus.partlyPaid:
      return <RequestQuote />;
    case paymentStatus.paid:
      return <PriceCheck />;
    default:
      return null;
  }
}

export function ignoreIsMissingInfo(status) {
  return status > orderStatus.invoicedInERP;
}

export function canUpdateOrderLineQty(orderLine, isOrderPrepay, orderPaymentStatus) {
  if (!orderLine.status || !orderLine.part?.partType) {
    // Could probably be considered an error. But false will suffice
    return false;
  }

  const isMissingPrepayArg = isOrderPrepay === undefined || isOrderPrepay === null;
  const isMissingPaymentStatusArg =
    !isMissingPrepayArg && isOrderPrepay && (orderPaymentStatus === undefined || orderPaymentStatus === null);
  const hasInvalidPrepayArgs = isMissingPrepayArg || isMissingPaymentStatusArg;
  if (hasInvalidPrepayArgs) {
    // To avoid isPrepay not being specified, or if it's defined we require paymentStatus as well
    return false;
  }

  if (
    isOrderPrepay &&
    (orderLine.status > orderStatus.waitingForPrePayment || orderPaymentStatus > paymentStatus.readyForInvoice)
  ) {
    return false;
  }

  const isFreight = isFreightPartType(orderLine.part.partType);
  if (isFreight) {
    return orderLine.status <= orderStatus.waitingForInvoice;
  }

  const isAutoPicked = isAutoPickPartType(orderLine.part.partType);
  if (isAutoPicked && orderLine.status <= orderStatus.picked) {
    return true;
  } else if (!isAutoPicked && orderLine.status < orderStatus.picked) {
    return true;
  }

  return false;
}

export function canUpdateOrderLineNotQty(orderLine, isOrderPrepay, orderPaymentStatus) {
  if (!orderLine.status || !orderLine.part?.partType) {
    // Could probably be considered an error. But false will suffice
    return false;
  }

  const isMissingPrepayArg = isOrderPrepay === undefined || isOrderPrepay === null;
  const isMissingPaymentStatusArg =
    !isMissingPrepayArg && isOrderPrepay && (orderPaymentStatus === undefined || orderPaymentStatus === null);
  const hasInvalidPrepayArgs = isMissingPrepayArg || isMissingPaymentStatusArg;
  if (hasInvalidPrepayArgs) {
    // To avoid isPrepay not being specified, or if it's defined we require paymentStatus as well
    return false;
  }

  if (
    isOrderPrepay &&
    (orderLine.status > orderStatus.waitingForPrePayment || orderPaymentStatus > paymentStatus.readyForInvoice)
  ) {
    return false;
  }

  return orderLine.status <= orderStatus.waitingForInvoice;
}

export function canUpdateOrderLine(orderLine, isOrderPrepay, orderPaymentStatus) {
  return (
    canUpdateOrderLineNotQty(orderLine, isOrderPrepay, orderPaymentStatus) ||
    canUpdateOrderLineQty(orderLine, isOrderPrepay, orderPaymentStatus)
  );
}
